import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { NotificationCenter } from '../NovuNotificationCtr';
import LoginModal from '../../pages/onboarding-module/modals/LoginModal';
import { useOnboarding } from '../redux/hooks/useOnboarding';
import PostHogProvider from '../analytics/PostHogProvider';

/**
 * OnboardingLayout - A minimal layout for onboarding flow
 * Features a simple navbar with logo, notification icon, and user avatar
 * No sidebar navigation
 * Includes PostHog analytics tracking (HIPAA-compliant, only for onboarding)
 */
const OnboardingLayout = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  const { 
    isAuthenticated, 
    authenticatedUser,
    clearAuthenticationState,
    resetSession,
    resetOrganization,
    resetUser,
    resetFeatures,
    resetDashboard,
    clearEhrCredentialsState
  } = useOnboarding();
  
  const location = useLocation();
  const isDashboard = location.pathname === '/onboarding/dashboard';
  const isEHRConnect = location.pathname === '/onboarding/ehr-connect';
  const showNavTabs = isDashboard || isEHRConnect;

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleSignOut = () => {
    // Clear all user-related data from Redux
    clearAuthenticationState();
    resetSession();
    resetOrganization();
    resetUser();
    resetFeatures();
    resetDashboard();
    clearEhrCredentialsState();
    
    // Close dropdown
    setIsProfileDropdownOpen(false);
    
    // Navigate to home or login page
    navigate('/');
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <PostHogProvider>
      <div className="onboarding-layout">
        {/* Minimal Navbar */}
        <header className="onboarding-navbar">
          <div className="onboarding-navbar-content">
            {/* Logo */}
            <div className="onboarding-logo">
              <a href="/">
                <img 
                  src="/dist/assets/images/logo-sm.svg" 
                  alt="Ankr Logo" 
                  style={{ maxHeight: '28px' }} 
                />
                <span className="logo-text">Ankr</span>
              </a>
            </div>

            {/* Navigation Tabs - Show for Dashboard and EHR Connect */}
            {showNavTabs && (
              <nav className="onboarding-nav-tabs">
                <Link 
                  to="/onboarding/dashboard" 
                  className={`onboarding-nav-tab ${isDashboard ? 'active' : ''}`}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/onboarding/ehr-connect" 
                  className={`onboarding-nav-tab ${isEHRConnect ? 'active' : ''}`}
                >
                  EHR Connect
                </Link>
                <a href="#" className="onboarding-nav-tab">Settings</a>
                <a href="#" className="onboarding-nav-tab">Help</a>
              </nav>
            )}

            {/* Right side - Notification & Avatar */}
            <div className="onboarding-navbar-actions">
              {/* <NotificationCenter /> */}
              
              {/* Show avatar if authenticated, otherwise show login button */}
              {isAuthenticated ? (
                <div className="position-relative" ref={dropdownRef}>
                  <div className="d-flex align-items-center gap-2">
                    <img 
                      className="rounded-circle cursor-pointer" 
                      src="/dist/assets/images/users/avatar-1.jpg"
                      alt={`${authenticatedUser?.firstName || 'User'} ${authenticatedUser?.lastName || ''}`}
                      title={authenticatedUser?.email}
                      onClick={toggleProfileDropdown}
                      style={{ 
                        width: '36px', 
                        height: '36px', 
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    />
                    {authenticatedUser?.firstName && (
                      <span className="user-name-text" style={{ marginLeft: '8px' }}>
                        {authenticatedUser.firstName} {authenticatedUser.lastName}
                      </span>
                    )}
                  </div>
                  
                  {/* Profile Dropdown Menu */}
                  {isProfileDropdownOpen && (
                    <div 
                      className="position-absolute end-0 mt-2 bg-white border rounded shadow-lg"
                      style={{ 
                        right: '0', 
                        top: '100%', 
                        minWidth: '200px', 
                        zIndex: 1000,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <div className="p-3 border-bottom" style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <div className="fw-semibold" style={{ fontSize: '14px', fontWeight: 600 }}>
                          {authenticatedUser?.firstName} {authenticatedUser?.lastName}
                        </div>
                        <div className="text-muted small" style={{ fontSize: '12px', color: '#6b7280' }}>
                          {authenticatedUser?.email}
                        </div>
                      </div>
                      <div className="py-1">
                        <button
                          className="w-full text-start px-3 py-2 text-decoration-none border-0 bg-transparent d-flex align-items-center gap-2"
                          style={{ 
                            padding: '8px 12px', 
                            fontSize: '14px', 
                            color: '#dc2626',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={handleSignOut}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <span style={{ fontSize: '16px' }}>🚪</span>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button className="onboarding-nav-tab" onClick={handleLoginClick}>Login</button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area - No sidebar */}
        <main className="onboarding-main-content">
          <Outlet />
        </main>

        {/* Login Modal */}
        <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseLoginModal} />
      </div>
    </PostHogProvider>
  );
};

export default OnboardingLayout;
